import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Save, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import ImagePicker from "@/components/admin/ImagePicker";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", image: "", published: false, published_at: "", seo_title: "", meta_description: "" };

const toLocalInput = (iso) => (iso ? iso.slice(0, 16) : "");

export default function BlogEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(EMPTY);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const isNew = !id;

  useEffect(() => {
    if (!id) return;
    api.get("/admin/blog").then((r) => {
      const found = r.data.find((x) => x.id === id);
      if (found) setPost({ ...EMPTY, ...found, published_at: toLocalInput(found.published_at) });
      else toast.error("Article introuvable");
      setLoading(false);
    }).catch((e) => { toast.error(apiError(e)); setLoading(false); });
  }, [id]);

  const set = (k) => (e) => setPost((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...post, published_at: post.published_at ? new Date(post.published_at).toISOString() : null };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      if (isNew) {
        const { data } = await api.post("/admin/blog", payload);
        toast.success("Article créé");
        navigate(`/admin/blog/${data.id}`, { replace: true });
      } else {
        await api.put(`/admin/blog/${id}`, payload);
        toast.success("Article enregistré");
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted-foreground">Chargement…</p>;

  return (
    <div data-testid="admin-blog-edit" className="max-w-3xl">
      <button onClick={() => navigate("/admin/blog")} data-testid="blog-back"
        className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-muted-foreground hover:text-rouge mb-6">
        <ArrowLeft className="w-4 h-4" /> Retour au blog
      </button>
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-onyx mb-8">
        {isNew ? "Nouvel article" : "Modifier l'article"}
      </h1>

      <div className="bg-white border border-line p-6 sm:p-8 space-y-5">
        <div>
          <Label>Titre *</Label>
          <Input data-testid="post-title" className="mt-1.5" value={post.title} onChange={set("title")} />
        </div>
        <div>
          <Label>Slug (URL) — laisser vide pour générer automatiquement</Label>
          <Input data-testid="post-slug" className="mt-1.5 font-mono text-sm" value={post.slug} onChange={set("slug")} placeholder="mon-super-article" />
        </div>
        <div>
          <Label>Résumé (chapeau)</Label>
          <Textarea rows={2} data-testid="post-excerpt" className="mt-1.5" value={post.excerpt} onChange={set("excerpt")} />
        </div>
        <ImagePicker label="Image de l'article" value={post.image} onChange={(url) => setPost((p) => ({ ...p, image: url }))} />
        <div>
          <Label>Contenu (paragraphes séparés par une ligne vide)</Label>
          <Textarea rows={12} data-testid="post-content" className="mt-1.5" value={post.content} onChange={set("content")} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 border-t border-line pt-5">
          <div className="flex items-center gap-3">
            <Switch data-testid="post-published" checked={!!post.published} onCheckedChange={(v) => setPost((p) => ({ ...p, published: v }))} />
            <Label>Publié</Label>
          </div>
          <div>
            <Label>Date de publication</Label>
            <Input type="datetime-local" data-testid="post-published-at" className="mt-1.5" value={post.published_at || ""} onChange={set("published_at")} />
          </div>
        </div>
        <div className="border-t border-line pt-5 space-y-5">
          <p className="font-display font-bold text-onyx">SEO</p>
          <div>
            <Label>Titre SEO (optionnel)</Label>
            <Input data-testid="post-seo-title" className="mt-1.5" value={post.seo_title} onChange={set("seo_title")} />
          </div>
          <div>
            <Label>Meta description (optionnel)</Label>
            <Textarea rows={2} data-testid="post-meta-description" className="mt-1.5" value={post.meta_description} onChange={set("meta_description")} />
          </div>
        </div>
        <button onClick={save} disabled={saving || !post.title} data-testid="post-save"
          className="inline-flex items-center gap-2 bg-rouge text-white px-8 py-3.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </div>
  );
}
